import { useCallback } from 'react'

import { createLoadOnceAPIHook, useStoreValue } from '@/lib/react-hooks/generic-hooks'
import { PackagesAndBillingSettingsActions, PackagesAndBillingSettingsTypes } from '@/redux/actions'
import { createLoadingSelector } from '@/redux/reducers/loading.reducer'
import { ApplicationState } from '@/redux/stores/types'
import { FEATURE_SCOPE_WEIGHT } from '@/travelsuit'
import { FeatureScope } from '@/types/billing'
import { CompanyInvoiceStatus } from '@/types/invoiceProfile'

const useLoadPackagesAndBillingSettingsOnce = createLoadOnceAPIHook<{}, ApplicationState['packagesAndBillingSettings']>(
	{
		loadAction: (_, { onLoad, onError }) =>
			PackagesAndBillingSettingsActions.getPackagesAndBillingSettings(onLoad, onError),
		loadingSelectors: PackagesAndBillingSettingsTypes.GetPackagesAndBillingSettings,
		storeMapper: ({ packagesAndBillingSettings }) => packagesAndBillingSettings,
		isEmpty: (settings) => !settings.length,
	},
)

const loadingSelector = createLoadingSelector([PackagesAndBillingSettingsTypes.GetPackagesAndBillingSettings.REQUEST])

const checkFeaturePermission = (
	packagesAndBillingSettings: ApplicationState['packagesAndBillingSettings'],
	requiredFeature?: FeatureScope,
) => {
	return packagesAndBillingSettings.some(
		({ package: { feature_scope } }) =>
			!requiredFeature || FEATURE_SCOPE_WEIGHT[feature_scope] >= FEATURE_SCOPE_WEIGHT[requiredFeature],
	)
}

export function useCheckFeaturePermission() {
	const isLoading = useStoreValue((state) => loadingSelector(state.loading))
	const settings = useLoadPackagesAndBillingSettingsOnce({})
	const packagesAndBillingSettings = useStoreValue((state) => state.packagesAndBillingSettings)

	return useCallback(
		(requiredFeature?: FeatureScope) => {
			const isPermitted = checkFeaturePermission(packagesAndBillingSettings, requiredFeature)

			return {
				isPermitted,
				isLoading: isLoading || !settings.length,
			}
		},
		[isLoading, settings.length, packagesAndBillingSettings],
	)
}

export function useIsOnboarding() {
	const paymentInfos = useLoadPackagesAndBillingSettingsOnce({})
	return !paymentInfos.some((pi) => pi.invoice_profile?.status === CompanyInvoiceStatus.Active)
}
